import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { FC, useMemo, useState } from "react";

import {
  CountTotalResult,
  countHeroTotal,
  countOpponentTotal,
  countSubOpponentsTotal,
  sumOpponentTotal,
} from "@/lib/statistics-analytic-utils";
import { useSummaryRows } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/hooks/use-summary-rows";
import { Currency } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { HeroResultsDto, PeriodStatisticsDto } from "@/shared/api";

interface Props {
  data: Array<PeriodStatisticsDto>;
}

const SummaryTable: FC<Props> = ({ data }) => {
  const heroTotal = useMemo<HeroResultsDto>(() => countHeroTotal(data), [data]);

  const opponentTotal = useMemo<CountTotalResult>(() => countOpponentTotal(data), [data]);

  const subOpponentsTotals = useMemo<HeroResultsDto[]>(
    () => countSubOpponentsTotal(data).map((i) => sumOpponentTotal(i)),
    [data]
  );

  const [currency, setCurrency] = useState<Currency>("Chips");

  const rows = useSummaryRows(heroTotal, opponentTotal, currency);

  return (
    <>
      <FormControl sx={{ width: 320, minWidth: 320, marginBottom: 2 }} size="small">
        <InputLabel id="demo-simple-select-label">Currency</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={currency}
          label="Currency"
          onChange={(e) => setCurrency(e.target.value as Currency)}
        >
          <MenuItem value="Chips">Chips</MenuItem>
          <MenuItem value="BB">BB</MenuItem>
          <MenuItem value="Usd">Usd</MenuItem>
        </Select>
      </FormControl>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={2} />
              <TableCell align="center" colSpan={3}>
                Hero/Total
              </TableCell>
              <TableCell colSpan={1} />
              <TableCell align="center" colSpan={4}>
                Losers
              </TableCell>
              <TableCell colSpan={1} />
              <TableCell align="center" colSpan={4}>
                Winners
              </TableCell>
              {subOpponentsTotals.length > 0 && (
                <>
                  <TableCell colSpan={1} />
                  <TableCell align="center" colSpan={subOpponentsTotals.length}>
                    SubOpponents
                  </TableCell>
                </>
              )}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</TableCell>
              <TableCell align="left">Hero</TableCell>
              <TableCell align="left">Losers Total</TableCell>
              <TableCell align="left">Winners Total</TableCell>
              <TableCell>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</TableCell>
              <TableCell align="left">Weak Losers</TableCell>
              <TableCell align="left">Middle Losers</TableCell>
              <TableCell align="left">Strong Losers</TableCell>
              <TableCell align="left">Unknown Losers</TableCell>
              <TableCell>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</TableCell>
              <TableCell align="left">Weak Winners</TableCell>
              <TableCell align="left">Middle Winners</TableCell>
              <TableCell align="left">Strong Winners</TableCell>
              <TableCell align="left">Unknown Winners</TableCell>
              {subOpponentsTotals.length > 0 && (
                <>
                  <TableCell>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</TableCell>
                  {subOpponentsTotals.map((_, index) => (
                    <TableCell key={index} align="left">
                      SubOp {index + 1}
                    </TableCell>
                  ))}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              return (
                <TableRow key={index}>
                  <TableCell>{row.label}</TableCell>
                  {row.data.map((cell, idx) => {
                    return (
                      <>
                        <TableCell align="left" key={idx}>
                          {cell}
                        </TableCell>
                      </>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SummaryTable;
